/* eslint-disable jsx-a11y/control-has-associated-label */
import classNames from 'classnames';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { exchangeDocumentIdForAccessibleLink } from '@steelbuy/api-integration';
import { useAuth } from '@steelbuy/auth';
import { Literal } from '@steelbuy/ts-shared';
import {
    ButtonGhostSystemOnLightS,
    ButtonSize,
    ButtonTertiary,
    Icon,
    IconIdentifier,
    Tooltip,
    TooltipType,
} from '@steelbuy/ui-primitive';
import { startDownload } from '@steelbuy/util';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';
import { CollectionAddressCell } from './CollectionAddressCell';
import { ListingCompleteness } from './ListingCompleteness';
import { constants } from '../../../constants';
import { BulkListing, getCompletenessItems, AllTableHeaders, BulkUploadForm } from '../bulkListingUtil';

import './BulkListingTable.scss';

const CELL_PADDING_PX = 16;
const COMPLETENESS_CELL_PADDING_PX = 18;

const COMPLETENESS_WIDTH = 124;
const DELETE_WIDTH = 32;

const CELLS_WITH_MAX_WIDTH = new Set(['description', 'collectionAddress']);

const resetWidthsOfCells = (cells: HTMLTableCellElement[]) => {
    cells.forEach((cell) => {
        const td = cell;

        td.style.minWidth = 'unset';
        td.style.maxWidth = 'unset';
    });
};

const RequiredCell = () => {
    const { t } = useTranslation('uiDomain');
    return (
        <div className="bulk-listing-table__table__missing-field">
            <Icon name={IconIdentifier.ERROR} />
            <span>{t('validation.required')}</span>
        </div>
    );
};

const ViewFileCell = ({
    listing,
    isPrime,
    field = 'testCertificate',
}: {
    listing: BulkListing;
    isPrime: boolean;
    field: 'testCertificate' | 'picture';
}) => {
    const { t } = useTranslation(['translation', 'uiDomain']);
    const auth = useAuth();

    const isFieldTestCertificate = field === 'testCertificate';

    if (isFieldTestCertificate && listing.certificateToFollow) {
        return <td>{t('application.createBulkListing.fields.testCertToFollow')}</td>;
    }

    if (isFieldTestCertificate && isPrime && listing.testCertificate === undefined) {
        return (
            <td>
                <RequiredCell />
            </td>
        );
    }

    if (field in listing && listing[field]?.documentId) {
        return (
            <td>
                <ButtonTertiary
                    label={t('uiDomain:fileInput.viewButton')}
                    icon={IconIdentifier.EYE}
                    size={ButtonSize.S}
                    onClick={async (e) => {
                        e.stopPropagation();
                        if (listing[field]?.documentId) {
                            const fileDownload = await exchangeDocumentIdForAccessibleLink(
                                constants.apiBaseUrl,
                                listing[field]?.documentId,
                                auth.getToken().get()
                            );
                            startDownload(fileDownload?.url.value, fileDownload?.meta.fileName);
                        }
                    }}
                />
            </td>
        );
    }

    if (!isPrime) {
        return (
            <td className="bulk-listing-table__table__not-applicable">
                {t('application.createBulkListing.editListings.notApplicable')}
            </td>
        );
    }

    return <td />;
};

export const BulkListingTable = ({
    listingsData,
    formData,
    onRowClick,
    showCompleteness,
    tableHeadings,
    onDelete,
}: {
    listingsData: BulkListing[];
    formData?: BulkUploadForm;
    showCompleteness?: boolean;
    onRowClick?: (rowIndex: number) => void;
    tableHeadings: AllTableHeaders;
    onDelete?: (rowIndex: number) => void;
}) => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const headings = Object.keys(tableHeadings) as (keyof typeof tableHeadings)[];
    const isQualityCategoriesEnabled = useIsVendorFeatureSupported('NON_PRIME_CATEGORIES');

    const headerRef = useRef<HTMLTableSectionElement>(null);
    const bodyTableRef = useRef<HTMLTableElement>(null);
    const bodyWrapperRef = useRef<HTMLDivElement>(null);
    const headerTableRef = useRef<HTMLTableElement>(null);
    const completenessHeadRef = useRef<HTMLTableSectionElement>(null);
    const syncCounter = useRef(0);

    const syncWidths = () => {
        if (!headerRef.current || !bodyTableRef.current || !bodyWrapperRef.current) return;

        const headerCells = Array.from(headerRef.current.querySelectorAll('th')) as HTMLTableCellElement[];

        const bodyCells = Array.from(
            bodyTableRef.current.querySelectorAll('tr:first-child td')
        ) as HTMLTableCellElement[];

        bodyCells.forEach((cell, i) => {
            const th = headerCells[i];
            const td = cell;

            const isCompletenessCell = showCompleteness && i === bodyCells.length - 2;
            const isDeleteCell = showCompleteness && i === bodyCells.length - 1;

            if (th) {
                let thWidth = 0;
                let tdWidth = 0;
                if (isCompletenessCell) {
                    thWidth = COMPLETENESS_WIDTH - COMPLETENESS_CELL_PADDING_PX;
                    tdWidth = COMPLETENESS_WIDTH;
                } else if (isDeleteCell) {
                    thWidth = DELETE_WIDTH;
                    tdWidth = DELETE_WIDTH;
                } else {
                    const width = Math.max(th.offsetWidth, td.offsetWidth) - CELL_PADDING_PX;
                    thWidth = width;
                    tdWidth = width;
                }

                th.style.minWidth = `${thWidth}px`;
                th.style.maxWidth = `${thWidth}px`;
                td.style.minWidth = `${tdWidth}px`;
                td.style.maxWidth = `${tdWidth}px`;
            }
        });

        if (showCompleteness && completenessHeadRef.current) {
            const cells = Array.from(
                completenessHeadRef.current.querySelectorAll('tr:first-child th')
            ) as HTMLTableCellElement[];

            const cell0Width = COMPLETENESS_WIDTH - CELL_PADDING_PX;
            cells[0].style.minWidth = `${cell0Width}px`;
            cells[0].style.maxWidth = `${cell0Width}px`;

            const cell1Width = bodyCells[bodyCells.length - 1].offsetWidth - CELL_PADDING_PX;
            cells[1].style.minWidth = `${cell1Width}px`;
            cells[1].style.maxWidth = `${cell1Width}px`;

            const tableWrapperWidth = bodyWrapperRef.current.getBoundingClientRect().width;
            const tableWidth = bodyTableRef.current.getBoundingClientRect().width;
            const completenessRight = tableWrapperWidth > tableWidth ? tableWrapperWidth - tableWidth : 0;
            completenessHeadRef.current.style.right = completenessRight > 0 ? `${completenessRight}px` : '0px';
        }
        syncCounter.current += 1;

        // due to table being 100% width, we need to run this a few times to ensure widths are properly synced
        if (syncCounter.current < 3) {
            requestAnimationFrame(syncWidths);
        }
    };

    const onTableResize = () => {
        syncCounter.current = 0;
        syncWidths();
    };

    useEffect(() => {
        onTableResize();

        window.addEventListener('resize', onTableResize);

        const wrapper = bodyWrapperRef.current;
        if (wrapper) {
            const syncScroll = () => {
                if (headerTableRef.current) {
                    headerTableRef.current.style.transform = `translateX(-${wrapper.scrollLeft}px)`;
                }
            };
            wrapper.addEventListener('scroll', syncScroll);
            return () => {
                window.removeEventListener('resize', onTableResize);
                wrapper.removeEventListener('scroll', syncScroll);
            };
        }
        return undefined;
    }, []);

    useEffect(() => {
        if (!headerRef.current || !bodyTableRef.current) return;
        const headerCells = Array.from(
            headerRef.current.querySelectorAll('tr:first-child th')
        ) as HTMLTableCellElement[];

        const bodyCells = Array.from(
            bodyTableRef.current.querySelectorAll('tr:first-child td')
        ) as HTMLTableCellElement[];
        resetWidthsOfCells(headerCells);
        resetWidthsOfCells(bodyCells);
        onTableResize();
    }, [listingsData]);

    return (
        <div className="bulk-listing-table">
            <div className="bulk-listing-table__wrapper">
                <div className="table-header-overlay">
                    <table className="bulk-listing-table__table" ref={headerTableRef}>
                        <thead ref={headerRef}>
                            <tr>
                                {headings.map((heading) => {
                                    const headingConfig = tableHeadings[heading];
                                    let headingText = '';
                                    if (headingConfig) {
                                        headingText =
                                            'heading' in headingConfig
                                                ? t(headingConfig.heading as Literal)
                                                : headingConfig.getHeading(formData as BulkUploadForm, t);
                                        if ('measurementLiteral' in headingConfig) {
                                            const literalText = t(`${headingConfig.measurementLiteral}` as Literal);
                                            headingText += ` (${literalText})`;
                                        } else if ('getMeasurementText' in headingConfig) {
                                            const literalText = headingConfig.getMeasurementText?.(
                                                formData as BulkUploadForm,
                                                t
                                            );
                                            headingText += ` (${literalText})`;
                                        }
                                    }
                                    return (
                                        <th
                                            key={heading}
                                            className={CELLS_WITH_MAX_WIDTH.has(heading) ? 'has-max-width' : ''}
                                        >
                                            {headingText}
                                        </th>
                                    );
                                })}
                                {showCompleteness && (
                                    <>
                                        <th>{t('application.createBulkListing.editListings.completeness')}</th>
                                        <th />
                                    </>
                                )}
                            </tr>
                        </thead>
                    </table>
                    {showCompleteness && (
                        <table className="bulk-listing-table__table">
                            <thead ref={completenessHeadRef} className="bulk-listing-table__completenessHead">
                                <tr>
                                    <th>{t('application.createBulkListing.editListings.completeness')}</th>
                                    <th />
                                </tr>
                            </thead>
                        </table>
                    )}
                </div>
                <div className="table-body" ref={bodyWrapperRef}>
                    <table
                        className={classNames('bulk-listing-table__table', {
                            'bulk-listing-table__table__completeness': showCompleteness,
                        })}
                        ref={bodyTableRef}
                    >
                        <tbody>
                            {listingsData.map((listing, rowIndex: number) => {
                                const completenessItems = getCompletenessItems(listing, isQualityCategoriesEnabled);
                                const { items } = completenessItems;
                                return (
                                    <tr
                                        key={`item-${rowIndex + 1}`}
                                        className={classNames('bulk-listing-table__row', {
                                            'bulk-listing-table__row--clickable': !!onRowClick,
                                        })}
                                        onClick={() => onRowClick?.(rowIndex)}
                                    >
                                        {headings.map((heading) => {
                                            let value = '';
                                            const headingConfig = tableHeadings[heading];
                                            let isMissingField = false;
                                            let isNotApplicableField = false;
                                            if (heading === 'testCertificate' || heading === 'picture') {
                                                return (
                                                    <ViewFileCell
                                                        listing={listing as BulkListing}
                                                        key={heading}
                                                        isPrime={!!formData?.prime}
                                                        field={heading}
                                                    />
                                                );
                                            }
                                            if (heading === 'collectionAddress') {
                                                return (
                                                    <CollectionAddressCell
                                                        key={heading}
                                                        addressId={listing.pickupAddress?.id ?? ''}
                                                        onAddressLoaded={onTableResize}
                                                    />
                                                );
                                            }
                                            if (
                                                headingConfig &&
                                                'literalPath' in headingConfig &&
                                                heading in listing &&
                                                listing[heading as keyof typeof listing] !== undefined
                                            ) {
                                                value = t(
                                                    `${headingConfig.literalPath}.${
                                                        listing[heading as keyof typeof listing]
                                                    }` as Literal
                                                );
                                            } else if (headingConfig?.mapValue?.(listing)) {
                                                value = `${headingConfig.mapValue(listing)}`;
                                            } else {
                                                value =
                                                    heading in listing && !!listing[heading as keyof typeof listing]
                                                        ? String(listing[heading as keyof typeof listing])
                                                        : '';
                                            }
                                            if (value === '') {
                                                const itemForHeading = items.find((item) => item.field === heading);
                                                if (itemForHeading) {
                                                    isMissingField = true;
                                                } else {
                                                    isNotApplicableField = true;
                                                    value = t(
                                                        'application.createBulkListing.editListings.notApplicable'
                                                    );
                                                }
                                            }
                                            const showTooltip =
                                                CELLS_WITH_MAX_WIDTH.has(heading) && !isNotApplicableField;

                                            const cell = (
                                                <td
                                                    key={`td-${heading}`}
                                                    className={classNames({
                                                        'bulk-listing-table__table__not-applicable':
                                                            isNotApplicableField,
                                                        'bulk-listing-table__table__has-max-width':
                                                            !isNotApplicableField && CELLS_WITH_MAX_WIDTH.has(heading),
                                                    })}
                                                >
                                                    {isMissingField ? <RequiredCell /> : value}
                                                </td>
                                            );

                                            return showTooltip ? (
                                                <Tooltip content={value} type={TooltipType.INFO} key={heading}>
                                                    {cell}
                                                </Tooltip>
                                            ) : (
                                                cell
                                            );
                                        })}
                                        {showCompleteness && (
                                            <>
                                                <td>
                                                    <div className="bulk-listing-table__table__completeness__wrapper">
                                                        <ListingCompleteness completenessItems={completenessItems} />
                                                    </div>
                                                </td>
                                                <td>
                                                    <ButtonGhostSystemOnLightS
                                                        icon={IconIdentifier.BIN}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDelete?.(rowIndex);
                                                        }}
                                                    />
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
